// The shield skin (James, 2026-10-24: "the overshield back in a beautiful way, like the shield generators in the Dune
// movies"). A Holtzman-style personal shield: a second skin a couple of centimetres off the body, the body's own shape,
// so it hugs the form instead of sitting round it as a bubble. At rest it is nearly invisible: a scan sweeps it, a few
// glass cells catch the light here and there, a faint edge. A hit lights the cells around the strike and the reveal runs
// outward from it, a block at a time (DNEG revealed theirs by hand, frame by frame, "a natural dissipation from the
// strike point"). The break flares every cell white-hot and blows the skin outward as it dissolves. Shields are Dune's
// cold blue; the overshield power-up is a second, gold skin over it.
//
// One GLSL core (SHIELD_CORE) drives both users:
//  - cowboys: ShieldSkin, a skinned copy of the body (shield and overshield layers, one draw each, only while up)
//  - robots: robotShieldMaterial, a second draw of a robot type's instanced batch with the skin pushed off its parts
//    (one draw per type that has a shielded robot on screen, robots/index.ts)
// The rest of a hit (the sparks, the flare, the shatter shards) is shieldBlock / shieldShatter on the shared Fx pools.

import * as THREE from 'three';
import type { Fx } from './index';
import { CMode, COrient, CFlag } from './cards';
import { PShape, PFlag } from './particles';

/** linear-ish colours for the skins and the FX (the post pass tone-maps; these are pre-bloom) */
export const SHIELD_COL = [0.36, 0.72, 1.0] as const; // Dune's cold blue
export const SHIELD_HOT = [0.84, 0.94, 1.0] as const; // the hot edge
/** the same field on a bright body (the cream robots): deeper, so the glass tints instead of vanishing into it */
export const SHIELD_DEEP = [0.1, 0.38, 1.0] as const;
export const OVER_COL = [1.0, 0.5, 0.06] as const; // the overshield power-up's gold, a deep amber so it separates from the meadow
export const OVER_HOT = [1.0, 0.94, 0.72] as const; // champagne edges over the amber, so it breaks from the grass

/**
 * The look, as one function. bp = body-space position in heights (y 0 feet .. 1 crown; the cells stick to the body as it
 * moves), wp = world position, fres = 0 facing .. 1 grazing. k = shield fraction (0 off .. 1 full); hit = world strike
 * point + seconds since (w < 0: none); brk = seconds since it broke (< 0: not); regen = seconds since it came back;
 * drain = seconds since it was poured forward (the Juggernaut's Charge; z = body forward in heights); hs = hit scale (1 a cowboy, more for a robot, struck on its hit sphere).
 * Returns x = the cold glass intensity, y = the lit pane edges (hot).
 */
export const SHIELD_CORE = /* glsl */ `
float shH( vec3 c ) { return fract( sin( dot( c, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 ); }
// printed steps, not a soft glow (the screenprint pass posterizes the world; the skin is inked the same way)
float shStep( float x ) { return floor( clamp( x, 0.0, 1.0 ) * 3.0 + 0.35 ) / 3.0; }
// returns x = cold intensity (the tinted glass), y = hot (the lit pane edges)
vec2 duneShield( vec3 bp, vec3 wp, float fres, float t, float k, vec4 hit, float brk, float regen, float drain, float seed, float hs ) {
  // glass panes: wide, short rectangles on a sheet wrapped round his vertical axis (a 3D grid sliced by a curved body
  // gives wiggly edges; this gives Dune's rectangular panes), eight columns round him, a staggered course every other row
  float ang = atan( bp.x, bp.z ) * 0.159155 * 8.0;
  float row = bp.y * 22.0;
  float stag = 0.5 * mod( floor( row ), 2.0 );
  vec2 g1 = vec2( ang + stag, row );
  vec3 c1 = vec3( floor( g1 ), 0.0 );
  float h = shH( c1 + seed );
  vec2 f1 = fract( g1 );
  vec2 e2 = min( f1, 1.0 - f1 );
  float edge = min( e2.x, e2.y ); // distance to the nearest pane wall, in pane units
  float lw = max( fwidth( edge ), 1e-4 );
  // a crisp 1-2 px pane edge; once a pane is only a few pixels across the edges would merge into a wash, so they thin
  // out and the pane's own tint carries it (far = 0 near .. 1 when the edges are gone)
  float far = smoothstep( 0.1, 0.25, lw );
  float line = ( 1.0 - smoothstep( lw * 0.6, lw * 1.6, edge ) ) * ( 1.0 - 0.75 * far );
  // a lit pane is a flat, printed fill in one of two tones with a thin hard edge: chunky stained glass, not wire
  float pane = 0.3 + 0.25 * step( 0.5, fract( h * 7.31 ) ) + 0.45 * line;
  float big = shH( floor( bp * vec3( 5.0, 9.0, 5.0 ) ) + seed * 1.7 );
  float rim = pow( fres, 3.0 );
  float cold = 0.0, hot = 0.0;

  // ---- at rest: a scan band of panes rises through the body every few seconds (about a fifth of him at a time), lit
  // here and there; odd panes twinkle; the silhouette carries a thin film so you know it's up at range
  if ( k > 0.0 ) {
    float sy = fract( t * 0.34 + seed * 0.37 ) * 1.6 - 0.3;
    float dy = bp.y - sy;
    float band = exp( -dy * dy / 0.006 );
    float trail = step( dy, 0.0 ) * exp( dy / 0.12 ) * step( 0.8, h );
    float lit = shStep( band * step( 0.55, h ) * ( 0.6 + 0.4 * big ) + trail * 0.55 );
    float tw = step( 1.0 - 0.015 * k * k, shH( c1 + floor( t * 4.0 + h * 17.0 ) ) ); // the overshield (k > 1) glints more
    float rest = lit * pane + tw * pane * 0.6 + rim * 0.16 * step( 0.55, h ); // a broken rim, pane by pane, not an outline
    // a weak shield stutters (it is about to go)
    float stut = k < 0.34 ? step( 0.35, fract( sin( floor( t * 17.0 + seed ) * 91.7 ) * 437.1 ) ) * ( 0.5 + k ) : 1.0;
    cold += rest * ( 0.4 + 0.6 * k ) * stut;
    hot += lit * line * 0.35 * k * stut;
  }

  // ---- a strike: the panes at the contact point flare, and a ring of panes runs out from it and goes dark behind the
  // front. It stays local (a hand-span or two): a shot on the chest never lights the boots
  if ( hit.w >= 0.0 && hit.w < 0.7 && brk < 0.0 ) { // (the shot that breaks it plays the break instead)
    float d = distance( wp, hit.xyz );
    float front = ( 0.05 + hit.w * 2.0 ) * hs;
    float tr = front - ( d + h * 0.08 * hs );
    float reach = 1.0 - smoothstep( 0.4 * hs, 0.62 * hs, d );
    float ring = shStep( step( 0.0, tr ) * exp( -tr * 12.0 ) * step( 0.35, h ) * ( 1.0 - 0.5 * smoothstep( 0.1, 0.3, hit.w ) ) ) * reach;
    float fade = 1.0 - smoothstep( 0.2, 0.45, hit.w );
    float core = shStep( ( 1.0 - smoothstep( 0.07 * hs, 0.15 * hs, d + h * 0.03 ) ) * exp( -hit.w * 7.0 ) );
    cold += ( ring * pane + core * pane * 1.6 ) * fade;
    hot += ( ring * line * 0.6 + core * ( 0.6 + line * 0.4 ) ) * fade;
  }

  // ---- coming back up: a bright line rises from the feet and fills the skin in behind it
  if ( regen >= 0.0 && regen < 0.6 ) {
    float fy = regen / 0.45;
    float dl = bp.y - fy;
    float on = 1.0 - regen / 0.6;
    hot += step( abs( dl ), 0.025 ) * 0.7 * on;
    cold += step( dl, 0.0 ) * step( 0.55, h ) * pane * 0.5 * on;
  }

  // ---- poured forward (the Charge): the panes drain from his back to his front and are thrown off ahead of him
  if ( drain >= 0.0 && drain < 0.22 ) {
    float fz = mix( -0.35, 0.4, drain / 0.15 );
    float dz = bp.z - fz;
    float keep = step( 0.0, dz ) * step( 0.4, h ) * ( 1.0 - smoothstep( 0.15, 0.22, drain ) );
    cold += keep * step( 0.5, h ) * pane * 0.5;
    hot += step( abs( dz ), 0.03 ) * 0.8 * ( 1.0 - smoothstep( 0.12, 0.22, drain ) );
  }

  // ---- the break, in two beats: every pane edge flashes at once (a frame or two), then the panes go out from the strike
  // outward, a hot crack line riding the front, the skin gone by 0.4 s
  if ( brk >= 0.0 && brk < 0.5 ) {
    float dB = distance( wp, hit.xyz );
    if ( dB > 2.5 ) dB = abs( bp.y - 0.55 ) * 1.8; // never struck lately (a hazard took it): from his middle
    float r = 0.1 + brk * 6.0;
    float alive = step( r, dB + h * 0.35 ) * ( 1.0 - smoothstep( 0.25, 0.38, brk ) );
    float flash = ( 1.0 - smoothstep( 0.03, 0.09, brk ) ) * exp( -dB * 2.2 ); // hard where it broke, not the whole body
    float crack = step( abs( r - dB - h * 0.35 ), 0.08 ); // the panes on the front flare as they go
    cold += alive * step( 0.5, h ) * pane * 0.5 + flash * pane * 1.2 + crack * pane * 0.9;
    hot += flash * line * 0.6 + crack * 0.35;
  }
  return vec2( cold, hot );
}
`;

/** premultiplied alpha: rgb is added, alpha darkens what's behind (the tint), so one pass is film plus glow */
const GLASS = { blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor, blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor } as const;

// ------------------------------------------------------------------ cowboys
const SKIN_VERT = /* glsl */ `
#include <common>
#include <skinning_pars_vertex>
uniform float uGrow;
uniform vec2 uYRange;
varying vec3 vBp;
varying vec3 vW;
varying vec3 vN;
varying vec3 vV;
void main() {
  #include <beginnormal_vertex>
  #include <skinbase_vertex>
  #include <skinnormal_vertex>
  #include <begin_vertex>
  // body space in heights: the asset faces +Z, so z is forward; the cells ride the bind pose (they stick to the body)
  float H = max( uYRange.y - uYRange.x, 1e-3 );
  vBp = vec3( position.x / H, ( position.y - uYRange.x ) / H, position.z / H );
  transformed += normalize( normal ) * uGrow;
  #include <skinning_vertex>
  vec4 w = modelMatrix * vec4( transformed, 1.0 );
  vW = w.xyz;
  vN = normalize( mat3( modelMatrix ) * objectNormal );
  vec4 mv = viewMatrix * w;
  vV = -mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;
const SKIN_FRAG = /* glsl */ `
uniform float uTime; uniform float uK; uniform vec4 uHit; uniform float uBrk; uniform float uRegen; uniform float uDrain; uniform float uSeed;
uniform vec3 uCol; uniform vec3 uHot; uniform float uGain;
varying vec3 vBp; varying vec3 vW; varying vec3 vN; varying vec3 vV;
${SHIELD_CORE}
void main() {
  vec3 n = normalize( vN ), v = normalize( vV );
  float fres = 1.0 - clamp( abs( dot( n, v ) ), 0.0, 1.0 );
  vec2 s = duneShield( vBp, vW, fres, uTime, uK, uHit, uBrk, uRegen, uDrain, uSeed, 1.35 );
  // tinted glass, not light: the cells are a blue film over what's behind them (premultiplied, so they read blue on
  // the gold meadow instead of burning to white), and only the hot edges add light
  float dist = length( vV );
  float far = 1.0 - 0.6 * smoothstep( 14.0, 45.0, dist ); // a posse across the island isn't a row of lanterns
  float a = clamp( s.x * 0.5, 0.0, 0.45 ) * uGain * far;
  gl_FragColor = vec4( uCol * a + mix( uCol, uHot, 0.45 ) * min( s.y, 1.0 ) * 0.32 * uGain * far, a );
}
`;

function skinMaterial(col: readonly number[], hot: readonly number[], gain: number, seed: number) {
  return new THREE.ShaderMaterial({
    vertexShader: SKIN_VERT, fragmentShader: SKIN_FRAG,
    uniforms: {
      uTime: { value: 0 }, uK: { value: 0 }, uHit: { value: new THREE.Vector4(0, 0, 0, -1) }, uBrk: { value: -1 }, uRegen: { value: -1 },
      uDrain: { value: -1 }, uSeed: { value: seed }, uGrow: { value: 0.02 }, uYRange: { value: new THREE.Vector2(0, 1.8) },
      uCol: { value: new THREE.Vector3(...col) }, uHot: { value: new THREE.Vector3(...hot) }, uGain: { value: gain },
    },
    transparent: true, depthWrite: false, side: THREE.FrontSide, ...GLASS,
  });
}

/** What a cowboy's shield is doing this frame (CowboyRS fields, filled by game.ts; all optional so labs can stage it). */
export interface ShieldState {
  /** shield fraction 0..1 */
  k: number;
  /** overshield fraction 0..1 */
  over: number;
  /** world strike point and seconds since the last hit on the shield (99: none) */
  hx: number; hy: number; hz: number; hitAge: number;
  /** seconds since the shield / the overshield broke (99: not lately) */
  brkAge: number; overBrkAge: number;
  /** seconds since the shield came back from empty */
  regenAge: number;
  /** seconds since the Juggernaut's Charge poured it forward */
  drainAge: number;
  /** the body is cloaked (the Phantom): the skin keeps to hits and breaks only, so it can't give him away */
  cloaked: boolean;
}

const SHELL_GROW = 0.032, OVER_GROW = 0.055; // a few cm off the skin, a thin rim outside his ink line

/**
 * A cowboy's two skins (shield, overshield): skinned copies of the body that ride its skeleton, children of the body so
 * they share its world matrix by construction. Built once per body; each draws only while it has something to show.
 */
export class ShieldSkin {
  readonly shell: THREE.SkinnedMesh;
  readonly over: THREE.SkinnedMesh;
  private ms: THREE.ShaderMaterial;
  private mo: THREE.ShaderMaterial;
  constructor(private body: THREE.SkinnedMesh, seed: number) {
    const g = body.geometry;
    g.computeBoundingBox();
    const bb = g.boundingBox!;
    this.ms = skinMaterial(SHIELD_COL, SHIELD_HOT, 0.8, seed);
    this.mo = skinMaterial(OVER_COL, OVER_HOT, 0.85, seed + 3.1);
    for (const m of [this.ms, this.mo]) m.uniforms.uYRange.value.set(bb.min.y, bb.max.y);
    this.mo.uniforms.uGrow.value = OVER_GROW; this.ms.uniforms.uGrow.value = SHELL_GROW;
    const mk = (m: THREE.ShaderMaterial, name: string, order: number) => {
      const s = new THREE.SkinnedMesh(g, m);
      s.bind(body.skeleton, body.bindMatrix);
      s.frustumCulled = false; s.castShadow = false; s.receiveShadow = false;
      s.renderOrder = order; s.name = name; s.visible = false;
      body.add(s);
      return s;
    };
    this.shell = mk(this.ms, 'cowboy-shield', 3);
    this.over = mk(this.mo, 'cowboy-overshield', 4);
  }

  update(s: ShieldState, visible: boolean, time: number) {
    const g = this.body.geometry; // the LOD swap
    this.shell.geometry = g; this.over.geometry = g;
    const ages = (u: THREE.ShaderMaterial['uniforms'], k: number, brk: number, hitOn: boolean) => {
      u.uTime.value = time; u.uK.value = k;
      // xyz stays after the hit: the break runs from the last strike; with none in the last second (a hazard, a slam)
      // it runs from his chest
      if (s.hitAge < 1) u.uHit.value.set(s.hx, s.hy, s.hz, hitOn && s.hitAge < 0.7 ? s.hitAge : -1);
      else { const e = this.body.matrixWorld.elements; u.uHit.value.set(e[12], e[13] + 1.1, e[14], -1); }
      u.uBrk.value = brk < 0.5 ? brk : -1;
    };
    // ---- the shield
    {
      const u = this.ms.uniforms;
      const rest = s.cloaked || s.over > 0.001 ? 0 : s.k; // under an overshield the gold is the field you see
      ages(u, rest, s.brkAge, s.over <= 0.001); // under an overshield the gold layer takes the hits
      u.uRegen.value = s.regenAge < 0.6 && !s.cloaked ? s.regenAge : -1;
      u.uDrain.value = s.drainAge < 0.22 ? s.drainAge : -1;
      const brk = s.brkAge < 0.5 ? 0.012 * (1 + s.brkAge * 6) : 0; // the break blows the skin outward
      u.uGrow.value = SHELL_GROW + brk;
      this.shell.visible = visible && (rest > 0.001 || u.uHit.value.w >= 0 || u.uBrk.value >= 0 || u.uRegen.value >= 0 || u.uDrain.value >= 0);
    }
    // ---- the overshield (gold), over the top
    {
      const u = this.mo.uniforms;
      const rest = s.cloaked ? 0 : (s.over > 0.001 ? 1.6 : 0); // the power-up is never faint: it's a gift (k > 1 lifts the rest look)
      ages(u, rest, s.overBrkAge, s.over > 0.001);
      u.uRegen.value = -1; u.uDrain.value = -1;
      u.uGrow.value = OVER_GROW + (s.overBrkAge < 0.5 ? 0.006 * (1 + s.overBrkAge * 6) : 0);
      this.over.visible = visible && (rest > 0.001 || u.uHit.value.w >= 0 || u.uBrk.value >= 0);
    }
  }

  dispose() {
    this.shell.removeFromParent(); this.over.removeFromParent();
    this.ms.dispose(); this.mo.dispose();
  }
}

// ------------------------------------------------------------------ robots
/**
 * The robot skin: the type batch's geometry and part texture drawn once more, pushed off every part along its normals.
 * Per-instance iSh = (shield 0..1, seconds since a hit, seconds since it broke, seed), iShHit = world strike point.
 * A robot with nothing to show folds its vertices away (the draw is all-or-nothing per type).
 */
export function robotShieldMaterial(partDecl: string, parts: THREE.DataTexture, height: number) {
  const vert = /* glsl */ `
#include <common>
${partDecl}
attribute vec4 aMat;
attribute vec4 iSh;
attribute vec3 iShHit;
uniform float uH;
varying vec3 vBp; varying vec3 vW; varying vec3 vN; varying vec3 vV; varying vec4 vSh; varying vec3 vHit;
void main() {
  vSh = iSh; vHit = iShHit;
  bool show = iSh.x > 0.001 || ( iSh.y >= 0.0 && iSh.y < 0.7 ) || ( iSh.z >= 0.0 && iSh.z < 0.5 );
  if ( !show || aMat.x > 0.5 ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; } // nothing to show / the ink hull
  float gl0;
  mat4 pm = rbPartMatrix( gl0 );
  vec3 lp = ( pm * vec4( position, 1.0 ) ).xyz;
  vec3 ln = normalize( mat3( pm ) * normal );
  vBp = vec3( lp.x / uH, lp.y / uH, lp.z / uH );
  float grow = 0.04 + ( iSh.z >= 0.0 && iSh.z < 0.5 ? 0.012 * ( 1.0 + iSh.z * 6.0 ) : 0.0 );
  lp += ln * grow;
  vec4 w = modelMatrix * instanceMatrix * vec4( lp, 1.0 );
  vW = w.xyz;
  vN = normalize( mat3( modelMatrix ) * mat3( instanceMatrix ) * ln );
  vec4 mv = viewMatrix * w;
  vV = -mv.xyz;
  gl_Position = projectionMatrix * mv;
}
`;
  const frag = /* glsl */ `
uniform float uTime; uniform vec3 uCol; uniform vec3 uHot;
varying vec3 vBp; varying vec3 vW; varying vec3 vN; varying vec3 vV; varying vec4 vSh; varying vec3 vHit;
${SHIELD_CORE}
void main() {
  vec3 n = normalize( vN ), v = normalize( vV );
  float fres = 1.0 - clamp( abs( dot( n, v ) ), 0.0, 1.0 );
  float hitAge = vSh.y < 0.7 ? vSh.y : -1.0, brk = vSh.z < 0.5 ? vSh.z : -1.0;
  // robots wear it a touch brighter at rest than the posse (they're the thing you're reading at range)
  vec2 s = duneShield( vBp, vW, fres, uTime + vSh.w * 3.0, vSh.x * 1.6, vec4( vHit, hitAge ), brk, -1.0, -1.0, vSh.w, 2.2 ); // a robot is struck on its hit sphere, a hand off its shell
  // a robot's shield is a threat you read at range: a faint film at the silhouette even between scans, and its hits
  // lit harder than the posse's (you're the one shooting it)
  s.x += pow( fres, 2.0 ) * 0.35 * vSh.x;
  if ( hitAge >= 0.0 ) s *= 1.0 + 0.4 * ( 1.0 - hitAge / 0.7 );
  float dist = length( vV );
  float far = 1.0 - 0.5 * smoothstep( 16.0, 50.0, dist );
  float a = clamp( s.x * 0.6, 0.0, 0.7 ) * far;
  gl_FragColor = vec4( uCol * a + uHot * min( s.y, 1.0 ) * 0.3 * far, a );
}
`;
  const m = new THREE.ShaderMaterial({
    vertexShader: vert, fragmentShader: frag,
    uniforms: {
      uParts: { value: parts }, uH: { value: Math.max(0.5, height) }, uTime: { value: 0 },
      uCol: { value: new THREE.Vector3(...SHIELD_DEEP) }, uHot: { value: new THREE.Vector3(...SHIELD_COL) },
    },
    transparent: true, depthWrite: false, side: THREE.FrontSide, ...GLASS,
  });
  return m;
}

// ------------------------------------------------------------------ the FX: a blocked shot, a break
const _v = new THREE.Vector3();
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

/**
 * A shot stopped by a shield: a hard star where it struck (the round stopped dead on the glass), a short bright core, the
 * round's energy spraying back off the face toward the shooter, and two or three panes knocked loose. The skin's own
 * flare and pane ring (SHIELD_CORE) start at the same point. (nx, ny, nz) = the surface normal at the strike, toward the
 * shooter.
 */
export function shieldBlock(fx: Fx, x: number, y: number, z: number, nx: number, ny: number, nz: number, gold = false, size = 1) {
  const Cd = fx.cards, P = fx.particles;
  const col = gold ? OVER_COL : SHIELD_COL, hot = gold ? OVER_HOT : SHIELD_HOT, deep = gold ? OVER_COL : SHIELD_DEEP;
  const s = size;
  // sit it a hair proud of the skin, on the shooter's side, so the body never covers the contact point
  x += nx * 0.06; y += ny * 0.06; z += nz * 0.06;
  // the round, stopped dead: a white-hot pop for two or three frames over a deep core laid on (not added), so the
  // contact reads on a cream robot as well as on dark armour; the skin flares its panes round it (SHIELD_CORE)
  Cd.spawn(CMode.Soft, COrient.Billboard, x, y, z, 0, 0, 0, 0.25, 0, 0.26 * s, 0.32 * s, deep as any, 0.85, deep as any, 0, 0, 0, 0, 0, 0.5);
  Cd.spawn(CMode.Flash, COrient.Billboard, x, y, z, 0, 0, 0, 0.1, 0, 0.16 * s, 0.32 * s, [1, 1, 1] as any, 1, hot as any, CFlag.Add, 6, Math.random(), 0, Math.random() * 6, 1.2); // the white-hot pop, a few frames
  // the round's energy sprays back off the face toward the shooter: short, slow enough to stay at the contact
  for (let i = 0; i < 9; i++) {
    _v.set(nx + rnd(-0.8, 0.8), ny + rnd(-0.5, 0.8), nz + rnd(-0.8, 0.8)).normalize();
    const sp = rnd(2, 5);
    P.spawn(x, y, z, _v.x * sp, _v.y * sp, _v.z * sp, rnd(0.14, 0.28), 0, 8, 3, -1e5,
      PShape.Spark, PFlag.Add | PFlag.Stretch, 0.04 * s, 0.02 * s, 0, hot as any, 1, col as any, 0.5, 1.4, 1);
  }
  // two or three panes knocked loose for a beat
  for (let i = 0; i < 3; i++) {
    _v.set(nx + rnd(-0.6, 0.6), ny + rnd(-0.3, 0.6), nz + rnd(-0.6, 0.6)).normalize();
    const sp = rnd(0.6, 1.6);
    P.spawn(x, y, z, _v.x * sp, _v.y * sp, _v.z * sp, rnd(0.25, 0.4), 0, 0, 3, -1e5,
      PShape.Shard, PFlag.Add, 0.06 * s, 0.02 * s, rnd(-8, 8), col as any, 0.9, col as any, 0, 1.6, 1);
  }
}

/**
 * A shield breaking: one hard flash where it went, and the glass panes blown off the body away from the shot that broke
 * it (dx, dz = toward the shooter; 0, 0 = all round), tumbling down and out by 0.5 s. (x, y, z) = feet; h = body height.
 */
export function shieldShatter(fx: Fx, x: number, y: number, z: number, h: number, gold = false, dx = 0, dz = 0) {
  const Cd = fx.cards, P = fx.particles;
  const col = gold ? OVER_COL : SHIELD_COL, hot = gold ? OVER_HOT : SHIELD_HOT;
  const cy = y + h * 0.6;
  const dl = Math.hypot(dx, dz), ux = dl > 1e-3 ? dx / dl : 0, uz = dl > 1e-3 ? dz / dl : 0;
  Cd.spawn(CMode.Flare, COrient.Billboard, x + ux * 0.25, cy, z + uz * 0.25, 0, 0, 0, 0.12, 0, h * 0.28, h * 0.16, hot as any, 1, col as any, CFlag.Add, 2, 0, 0, Math.random() * 6, 0.7);
  // the panes: thrown off the body's surface away from the shot, tumbling, falling, going out
  for (let i = 0; i < 34; i++) {
    let ox = rnd(-1, 1), oz = rnd(-1, 1);
    ox -= ux * 1.3; oz -= uz * 1.3; // most go out the far side (the shot drove them)
    const ol = Math.hypot(ox, oz) || 1; ox /= ol; oz /= ol;
    const yy = y + h * rnd(0.08, 1.0), r = h * 0.16;
    const sp = rnd(2, 5.5);
    P.spawn(x + ox * r, yy, z + oz * r, ox * sp, rnd(0.5, 3), oz * sp, rnd(0.35, 0.55), 0, 7, 1.4, -1e5,
      PShape.Shard, PFlag.Add, rnd(0.1, 0.18), 0.04, rnd(-14, 14), i % 4 === 0 ? hot as any : col as any, 1, col as any, 0, 1.1, 0.6);
  }
}

// ------------------------------------------------------------------ state from the snapshots
interface Track { k: number; over: number; brk: number; obrk: number; regen: number; drain: number; seen: number }
/** a cowboy's shield readings, written onto its CowboyRS (contracts.ts) */
export interface ShieldRS {
  shieldK?: number; overK?: number; shieldBrkAge?: number; overBrkAge?: number; shieldRegenAge?: number; shieldDrainAge?: number;
}

/**
 * Turns each cowboy's shield and overshield readings into the skin's moments, the same on every machine (the local
 * player's prediction and the posse's snapshots alike): a break when it drops to empty, a refill sweep when it comes back,
 * and for the Juggernaut, a Charge (a class move as it empties) pours it forward instead of breaking it.
 * update() returns bit 1 when the shield just broke and bit 2 when the overshield did, for the shatter FX.
 */
export class ShieldTracker {
  private m = new Map<number, Track>();
  private frame = 0;
  begin() { this.frame++; }
  update(r: ShieldRS, id: number, k: number, over: number, juggernaut: boolean, specialAge: number, time: number): number {
    let t = this.m.get(id);
    if (!t) { t = { k, over, brk: -99, obrk: -99, regen: -99, drain: -99, seen: 0 }; this.m.set(id, t); }
    let ev = 0;
    if (t.k > 0.005 && k <= 0.005) {
      if (juggernaut && specialAge < 0.4) t.drain = time; // the Charge burnt it: poured forward, not broken
      else { t.brk = time; ev |= 1; }
    } else if (t.k <= 0.005 && k > 0.005 && t.seen) t.regen = time;
    if (t.over > 0.005 && over <= 0.005) { t.obrk = time; ev |= 2; }
    t.k = k; t.over = over; t.seen = this.frame;
    r.shieldK = k; r.overK = over;
    r.shieldBrkAge = time - t.brk; r.overBrkAge = time - t.obrk; r.shieldRegenAge = time - t.regen; r.shieldDrainAge = time - t.drain;
    return ev;
  }
  /** forget cowboys not updated this frame (they left) */
  end() { for (const [id, t] of this.m) if (t.seen !== this.frame) this.m.delete(id); }
}
